import { useCallback, useEffect, useRef, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import {
  MagnifyingGlass,
  FunnelSimple,
  ArrowClockwise,
  X,
  WarningCircle,
  SpinnerGap,
  CalendarBlank,
  ArrowRight,
  CaretLeft,
  CaretRight,
  CaretDoubleLeft,
  CaretDoubleRight,
  PlusCircle,
  ClockCounterClockwise,
  ShieldWarning,
  ListBullets,
  Lightning,
  Eye,
} from "phosphor-react"

import { DateRangePicker, SearchableSelect } from "../../components"
import auditLogService from "../../services/auditLogService"

/* ─── action metadata ───────────────────────────────────────── */

const ACTION_LABELS = {
  // Auth
  "impersonate":                    "Impersonate User",
  // Users
  "user.create":                    "User Created",
  "user.update":                    "User Updated",
  "user.delete":                    "User Deleted",
  // Clients
  "client.create":                  "Client Created",
  "client.update":                  "Client Updated",
  "client.delete":                  "Client Deleted",
  // Leads
  "lead.create":                    "Lead Created",
  "lead.update":                    "Lead Updated",
  "lead.delete":                    "Lead Deleted",
  "lead.convert_to_client":         "Lead Converted to Client",
  // Roles
  "role.create":                    "Role Created",
  "role.update":                    "Role Updated",
  "role.delete":                    "Role Deleted",
  // Ad Accounts
  "adaccount.create":               "Ad Account Created",
  "adaccount.update":               "Ad Account Updated",
  "adaccount.delete":               "Ad Account Deleted",
  "adaccount.rename":               "Ad Account Renamed",
  "adaccount.reshare_bm":           "BM / BC ID Added",
  "adaccount.status_change":        "Ad Account Status Changed",
  "adaccount.identifier_decision":  "Identifier Decision Made",
  // Tickets
  "ticket.create":                  "Ticket Opened",
  "ticket.message_added":           "Ticket Message Added",
  "ticket.status_change":           "Ticket Status Changed",
  "ticket.delete":                  "Ticket Deleted",
  // Credit Accounts
  "credit_account.create":          "Credit Account Created",
  "credit_account.update":          "Credit Account Updated",
  "credit_account.delete":          "Credit Account Deleted",
  // Deposits
  "deposit.crypto":                 "Deposit · Crypto",
  "deposit.payoneer":               "Deposit · Payoneer",
  "deposit.bank_transfer":          "Deposit · Bank Transfer",
  // Withdrawals / Account
  "withdraw.request":               "Withdraw Request",
  "account.keep_active":            "Keep Account Active",
  // Transfers
  "transfer.topup_created":         "Top-up Transfer Created",
  "transfer.withdrawal_created":    "Withdrawal Transfer Created",
  "transfer.status_change":         "Transfer Status Changed",
  // Expenses
  "expense.create":                 "Expense Created",
  "expense.update":                 "Expense Updated",
  "expense.delete":                 "Expense Deleted",
  // Payment methods
  "payment_method.update":          "Payment Method Updated",
}

const ACTION_COLORS = {
  // Red — destructive / high-risk
  "impersonate":                   "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  "user.delete":                   "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  "client.delete":                 "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  "lead.delete":                   "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  "role.delete":                   "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  "adaccount.delete":              "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  "ticket.delete":                 "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  "credit_account.delete":         "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  "expense.delete":                "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  // Green — creates
  "user.create":                   "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  "client.create":                 "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  "lead.create":                   "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  "lead.convert_to_client":        "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  "role.create":                   "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  "adaccount.create":              "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  "ticket.create":                 "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  "credit_account.create":         "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  "expense.create":                "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  // Yellow — updates / changes
  "user.update":                   "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  "client.update":                 "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  "lead.update":                   "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  "role.update":                   "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  "adaccount.update":              "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  "adaccount.rename":              "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  "adaccount.reshare_bm":          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  "adaccount.status_change":       "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  "adaccount.identifier_decision": "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  "ticket.status_change":          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  "ticket.message_added":          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  "credit_account.update":         "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  "expense.update":                "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  "payment_method.update":         "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  "transfer.status_change":        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  // Blue — deposits / transfers
  "deposit.crypto":                "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  "deposit.payoneer":              "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  "deposit.bank_transfer":         "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  "transfer.topup_created":        "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  // Orange — withdrawals / keep active
  "withdraw.request":              "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
  "transfer.withdrawal_created":   "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
  "account.keep_active":           "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
}

const DEFAULT_BADGE = "bg-neutral-100 text-neutral-800 dark:bg-neutral-900/30 dark:text-neutral-400"

const STAT_COLOR = {
  neutral: { text: "text-neutral-900 dark:text-neutral-200", bg: "bg-yellow-100 dark:bg-yellow-900/30" },
  blue:    { text: "text-blue-600 dark:text-blue-400",       bg: "bg-blue-100 dark:bg-blue-900/30" },
  red:     { text: "text-red-600 dark:text-red-600",         bg: "bg-red-100 dark:bg-red-900/30" },
}

/* ─── helpers ───────────────────────────────────────────────── */

const formatDate = (iso) => {
  if (!iso) return "N/A"
  const d = new Date(iso)
  if (isNaN(d)) return iso
  const datePart = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  }).format(d)
  const parts = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d)
  const h = parts.find((p) => p.type === "hour")?.value ?? "00"
  const m = parts.find((p) => p.type === "minute")?.value ?? "00"
  const suffix = parseInt(h, 10) < 12 ? "AM" : "PM"
  return `${datePart}, ${h}:${m} ${suffix}`
}

function getPageNumbers(current, total) {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1)
  if (current <= 3) return [1, 2, 3, 4, "...", total]
  if (current >= total - 2) return [1, "...", total - 3, total - 2, total - 1, total]
  return [1, "...", current - 1, current, current + 1, "...", total]
}

/* ─── Details Modal ─────────────────────────────────────────── */

function DetailsModal({ log, onClose }) {
  if (!log) return null

  const rows = [
    { label: "Timestamp",    value: formatDate(log.createdAt) },
    { label: "Action",       value: ACTION_LABELS[log.action] || log.action },
    { label: "Performed By", value: log.performedBy ? `${log.performedBy.fullName || "—"} (${log.performedBy.email})` : "System" },
    { label: "Target",       value: log.targetUser  ? `${log.targetUser.fullName  || "—"} (${log.targetUser.email})`  : "—" },
    { label: "IP Address",   value: log.ipAddress   || "—" },
    { label: "User Agent",   value: log.userAgent   || "—" },
  ]

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="w-full max-w-lg text-start rounded-xl shadow-lg bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800/30 overflow-hidden"
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0, transition: { duration: 0.18 } }}
          exit={{ opacity: 0, scale: 0.96, y: 8, transition: { duration: 0.14 } }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-150 dark:border-neutral-800/30">
            <div className="flex items-center gap-2">
              <ClockCounterClockwise size={18} className="text-neutral-600 dark:text-neutral-400" />
              <h3 className="text-sm font-[500] text-neutral-900 dark:text-white">Event Details</h3>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-xl text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          {/* Action badge */}
          <div className="px-6 pt-4 pb-2">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-[400] ${ACTION_COLORS[log.action] || DEFAULT_BADGE}`}>
              {ACTION_LABELS[log.action] || log.action}
            </span>
          </div>

          {/* Info rows */}
          <div className="px-6 pb-4 space-y-3">
            {rows.map(({ label, value }) => (
              <div key={label} className="flex items-start gap-3">
                <span className="text-xs text-neutral-500 dark:text-neutral-400 w-28 shrink-0 pt-0.5">{label}</span>
                <span className="text-sm font-[300] text-neutral-800 dark:text-white break-all">{value}</span>
              </div>
            ))}
          </div>

          {/* Details JSON */}
          {log.details && Object.keys(log.details).length > 0 && (
            <div className="mx-6 mb-4 rounded-xl border border-neutral-150 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/50 p-4">
              <p className="text-xs font-[500] text-neutral-600 dark:text-neutral-400 mb-2">Additional Details</p>
              <div className="space-y-2">
                {Object.entries(log.details).map(([key, val]) => (
                  <div key={key} className="flex items-start gap-3">
                    <span className="text-xs text-neutral-500 dark:text-neutral-400 w-36 shrink-0 capitalize pt-0.5">
                      {key.replace(/_/g, " ")}
                    </span>
                    <span className="text-xs font-[300] text-neutral-800 dark:text-neutral-200 break-all">
                      {typeof val === "object" ? JSON.stringify(val) : String(val ?? "—")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end px-6 py-4 border-t border-neutral-150 dark:border-neutral-800/30">
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-sm font-[400] rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-neutral-700 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

/* ─── sub-components ────────────────────────────────────────── */

function StatCard({ title, value, icon, color }) {
  const c = STAT_COLOR[color] || STAT_COLOR.neutral
  return (
    <div className="border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-start justify-center flex-col">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">{title}</p>
          <p className={`text-2xl font-normal ${c.text}`}>{Number(value || 0).toLocaleString("en-US")}</p>
        </div>
        <div className={`p-2 ${c.bg} rounded-xl`}>{icon}</div>
      </div>
    </div>
  )
}

function Th({ children }) {
  return (
    <th className="px-6 py-3 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
      {children}
    </th>
  )
}

function Td({ children, className = "" }) {
  return (
    <td className={`px-6 py-3 text-sm font-[300] text-neutral-800 dark:text-white text-start ${className}`}>
      {children}
    </td>
  )
}

function Chip({ icon, label, onClear }) {
  return (
    <div className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full text-xs">
      {icon}
      <span>{label}</span>
      <button onClick={onClear} className="ml-1 hover:text-blue-900 dark:hover:text-blue-100">
        <X size={12} />
      </button>
    </div>
  )
}

function PageBtn({ disabled, onClick, children, aria }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`p-1.5 rounded-xl ${
        disabled
          ? "text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
          : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
      }`}
      aria-label={aria}
    >
      {children}
    </button>
  )
}

/* ─── main component ────────────────────────────────────────── */

const AuditLog = () => {
  const reqIdRef = useRef(0)

  const [logs, setLogs]           = useState([])
  const [loading, setLoading]     = useState(false)
  const [actions, setActions]     = useState([])
  const [stats, setStats]         = useState({ total: 0, today: 0, security: 0 })
  const [selectedLog, setSelectedLog] = useState(null)

  // draft filters
  const [searchQuery, setSearchQuery]       = useState("")
  const [selectedAction, setSelectedAction] = useState("")
  const [dateRange, setDateRange]           = useState({ start: "", end: "" })
  const [showMoreFilters, setShowMoreFilters] = useState(false)

  // applied / pagination
  const [pagination, setPagination] = useState({
    currentPage: 1, pageSize: 10, totalPages: 1, totalItems: 0,
  })
  const [appliedFilters, setAppliedFilters] = useState({
    searchValue: "", action: "", dateRange: { start: "", end: "" },
  })
  const [refreshKey, setRefreshKey] = useState(0)

  const filtersActive =
    appliedFilters.searchValue || appliedFilters.action ||
    appliedFilters.dateRange.start || appliedFilters.dateRange.end

  /* page title */
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("setPageTitle", { detail: { title: "Audit Log" } }))
    return () => window.dispatchEvent(new CustomEvent("setPageTitle", { detail: { title: "Dashboard" } }))
  }, [])

  /* load action types + stats once */
  useEffect(() => {
    auditLogService.getActions()
      .then((r) => setActions(r?.data?.data || []))
      .catch((e) => console.error("Failed to load audit actions:", e))
    auditLogService.stats()
      .then((r) => { const d = r?.data?.data; if (d) setStats(d) })
      .catch((e) => console.error("Failed to load audit stats:", e))
  }, [])

  /* fetch logs */
  const fetchLogs = useCallback(async () => {
    const reqId = ++reqIdRef.current
    try {
      setLoading(true)
      const res = await auditLogService.search({
        searchValue: appliedFilters.searchValue,
        action:      appliedFilters.action,
        dateRange:   appliedFilters.dateRange,
        pagination:  { page: pagination.currentPage, limit: pagination.pageSize },
      })
      if (reqId !== reqIdRef.current) return
      const d = res?.data
      if (d?.statusCode === 200) {
        setLogs(d.data || [])
        if (d.pagination) {
          setPagination((p) => ({
            ...p,
            totalPages: d.pagination.totalPages ?? 1,
            totalItems: d.pagination.totalItems ?? 0,
          }))
        }
      }
    } catch {
      // silent
    } finally {
      if (reqId === reqIdRef.current) setLoading(false)
    }
  }, [appliedFilters, pagination.currentPage, pagination.pageSize, refreshKey]) // eslint-disable-line

  useEffect(() => { fetchLogs() }, [fetchLogs])

  /* handlers */
  const applyFilters = () => {
    setAppliedFilters({ searchValue: searchQuery, action: selectedAction, dateRange })
    setPagination((p) => ({ ...p, currentPage: 1 }))
    setRefreshKey((k) => k + 1)
  }

  const resetAndRefresh = () => {
    setSearchQuery(""); setSelectedAction(""); setDateRange({ start: "", end: "" })
    setAppliedFilters({ searchValue: "", action: "", dateRange: { start: "", end: "" } })
    setPagination((p) => ({ ...p, currentPage: 1 }))
    setRefreshKey((k) => k + 1)
  }

  const handleSearch = () => {
    setAppliedFilters((p) => ({ ...p, searchValue: searchQuery }))
    setPagination((p) => ({ ...p, currentPage: 1 }))
    setRefreshKey((k) => k + 1)
  }

  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.totalPages) return
    setPagination((p) => ({ ...p, currentPage: page }))
  }

  const handlePageSizeChange = (e) => {
    const size = parseInt(e.target.value, 10)
    setPagination((p) => ({ ...p, pageSize: size, currentPage: 1 }))
  }

  const actionOptions = [
    { id: "", name: "All actions", value: "" },
    ...actions.map((a) => ({ id: a, name: ACTION_LABELS[a] || a, value: a })),
  ]

  /* ─── render ─────────────────────────────────────────────── */
  return (
    <div className="lg:p-7 p-3 max-w-full mx-auto">

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        <StatCard title="Total Events"     value={stats.total}    icon={<ListBullets className="text-yellow-700" size={18} />} color="neutral" />
        <StatCard title="Today's Activity" value={stats.today}    icon={<Lightning    className="text-blue-600"   size={18} />} color="blue"    />
        <StatCard title="Security Events"  value={stats.security} icon={<ShieldWarning className="text-red-700"  size={18} />} color="red"     />
      </div>

      {/* Search + filter bar */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-grow max-w-xs">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <MagnifyingGlass size={16} className="text-neutral-800 dark:text-neutral-400" />
          </div>
          <input
            type="text"
            placeholder="Search by performer…"
            className="h-10 w-full border pl-10 pr-10 text-sm border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl placeholder-neutral-500 ring-neutral-300 dark:ring-neutral-700 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200 focus:outline-none focus:ring-1"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
            onClick={handleSearch}
          >
            <ArrowRight size={16} />
          </button>
        </div>

        <DateRangePicker
          onRangeChange={setDateRange}
          value={dateRange}
          className={dateRange.start || dateRange.end
            ? "border-blue-500 bg-blue-50 text-blue-600 dark:border-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
            : ""}
        />

        <button
          className={`flex items-center ring-yellow-500/50 gap-2 px-4 py-2.5 h-10 w-fit border text-sm rounded-xl cursor-pointer focus:outline-none focus:ring-1 ${
            showMoreFilters
              ? "border-yellow-500/50 bg-yellow-50 text-yellow-600 dark:border-yellow-600/30 dark:bg-yellow-900/20 dark:text-yellow-400"
              : "border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200"
          }`}
          onClick={() => setShowMoreFilters((v) => !v)}
        >
          <span className="text-sm">{showMoreFilters ? "Hide filters" : "More filters"}</span>
          {showMoreFilters
            ? <X size={16} className="text-yellow-600 dark:text-yellow-400" />
            : <PlusCircle size={16} className="text-neutral-800 dark:text-neutral-400" />}
        </button>

        <button
          className="flex items-center gap-2 h-10 px-4 border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl bg-white dark:bg-neutral-950 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-800"
          onClick={resetAndRefresh}
        >
          <ArrowClockwise size={16} className="text-neutral-800 dark:text-neutral-400" />
          <span className="text-sm">Reset</span>
        </button>

        {(searchQuery || selectedAction || dateRange.start || dateRange.end) && !showMoreFilters && (
          <button
            className="flex items-center text-xs justify-center px-4 gap-2 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400"
            onClick={applyFilters}
          >
            <FunnelSimple size={16} />
            <span className="text-xs">Apply Filters</span>
          </button>
        )}
      </div>

      {/* Active filter chips */}
      {filtersActive && (
        <div className="mb-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">Active filters:</span>

          {appliedFilters.searchValue && (
            <Chip icon={<MagnifyingGlass size={12} />} label={appliedFilters.searchValue} onClear={() => {
              setSearchQuery("")
              setAppliedFilters((p) => ({ ...p, searchValue: "" }))
              setRefreshKey((k) => k + 1)
            }} />
          )}

          {(appliedFilters.dateRange.start || appliedFilters.dateRange.end) && (
            <Chip icon={<CalendarBlank size={12} />}
              label={`${appliedFilters.dateRange.start ? new Date(appliedFilters.dateRange.start).toLocaleDateString() : "Any"} - ${appliedFilters.dateRange.end ? new Date(appliedFilters.dateRange.end).toLocaleDateString() : "Any"}`}
              onClear={() => {
                setDateRange({ start: "", end: "" })
                setAppliedFilters((p) => ({ ...p, dateRange: { start: "", end: "" } }))
                setRefreshKey((k) => k + 1)
              }}
            />
          )}

          {appliedFilters.action && (
            <Chip icon={<FunnelSimple size={12} />}
              label={ACTION_LABELS[appliedFilters.action] || appliedFilters.action}
              onClear={() => {
                setSelectedAction("")
                setAppliedFilters((p) => ({ ...p, action: "" }))
                setRefreshKey((k) => k + 1)
              }}
            />
          )}

          <button onClick={resetAndRefresh} className="text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1 ml-2">
            <X size={12} /><span>Clear all</span>
          </button>
        </div>
      )}

      {/* Expanded filters panel */}
      {showMoreFilters && (
        <div className="mb-6 p-6 border border-neutral-150 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-[300] text-neutral-800 dark:text-white">More filters</h2>
            <button onClick={resetAndRefresh} className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1.5">
              <X size={14} /><span>Clear all</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <SearchableSelect options={actionOptions} value={selectedAction} onChange={setSelectedAction} placeholder="Action type" icon={<FunnelSimple size={16} />} />
            <div className="md:col-span-2">
              <DateRangePicker onRangeChange={setDateRange} value={dateRange} />
            </div>
            <div className="flex items-center gap-2">
              <button
                className="flex items-center justify-center px-4 gap-2 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400"
                onClick={applyFilters}
              >
                <FunnelSimple size={16} /><span className="text-sm">Apply Filters</span>
              </button>
              <button
                className="flex items-center justify-center px-4 gap-2 h-10 border border-neutral-200 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700"
                onClick={resetAndRefresh}
              >
                <ArrowClockwise size={16} /><span className="text-sm">Reset</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-lg bg-white">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <SpinnerGap size={30} className="animate-[spin_0.8s_linear_infinite] text-neutral-600 dark:text-neutral-400 drop-shadow-sm" />
          </div>
        ) : logs.length > 0 ? (
          <>
            <div className="overflow-x-auto rounded-t-lg">
              <table className="min-w-full whitespace-nowrap divide-y divide-neutral-150 dark:divide-neutral-800">
                <thead className="bg-neutral-50 text-start h-14 dark:bg-neutral-900/30">
                  <tr>
                    <Th>Timestamp</Th>
                    <Th>Action</Th>
                    <Th>Performed By</Th>
                    <Th>Target</Th>
                    <Th>IP Address</Th>
                    <Th>Details</Th>
                  </tr>
                </thead>

                <tbody className="bg-white font-[300] dark:bg-neutral-950 divide-y divide-neutral-150 dark:divide-neutral-800">
                  {logs.map((log) => (
                    <tr key={log._id} className="hover:bg-neutral-50 text-start dark:hover:bg-neutral-800/50">

                      <Td className="text-neutral-500 dark:text-neutral-400 text-xs whitespace-nowrap">
                        {formatDate(log.createdAt)}
                      </Td>

                      <Td>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-[300] ${ACTION_COLORS[log.action] || DEFAULT_BADGE}`}>
                          {ACTION_LABELS[log.action] || log.action}
                        </span>
                      </Td>

                      <Td>
                        {log.performedBy ? (
                          <div className="text-start">
                            <div className="text-sm font-[400] text-neutral-800 dark:text-white">{log.performedBy.fullName || "—"}</div>
                            <div className="text-xs text-neutral-500 dark:text-neutral-400">{log.performedBy.email}</div>
                          </div>
                        ) : (
                          <span className="text-neutral-400 text-xs">System</span>
                        )}
                      </Td>

                      <Td>
                        {log.targetUser ? (
                          <div className="text-start">
                            <div className="text-sm font-[400] text-neutral-800 dark:text-white">{log.targetUser.fullName || "—"}</div>
                            <div className="text-xs text-neutral-500 dark:text-neutral-400">{log.targetUser.email}</div>
                          </div>
                        ) : (
                          <span className="text-neutral-400 text-xs">—</span>
                        )}
                      </Td>

                      <Td className="text-neutral-500 dark:text-neutral-400 text-xs">
                        {log.ipAddress || "—"}
                      </Td>

                      <Td>
                        <button
                          onClick={() => setSelectedLog(log)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                        >
                          <Eye size={13} />
                          View
                        </button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30">
              <div className="flex items-center mb-4 sm:mb-0">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  Showing {logs.length} of {pagination.totalItems} events
                </span>
                <div className="ml-4">
                  <select
                    value={pagination.pageSize}
                    onChange={handlePageSizeChange}
                    className="h-8 px-2 text-xs border border-neutral-200 dark:bg-neutral-800 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-white"
                  >
                    <option value={10}>10 / page</option>
                    <option value={25}>25 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <PageBtn disabled={pagination.currentPage === 1} onClick={() => handlePageChange(1)} aria="First page"><CaretDoubleLeft size={16} /></PageBtn>
                <PageBtn disabled={pagination.currentPage === 1} onClick={() => handlePageChange(pagination.currentPage - 1)} aria="Previous page"><CaretLeft size={16} /></PageBtn>

                <div className="flex items-center space-x-1">
                  {getPageNumbers(pagination.currentPage, pagination.totalPages).map((page, idx) =>
                    page === "..." ? (
                      <span key={`e-${idx}`} className="px-2 py-1 text-neutral-500 dark:text-neutral-400">...</span>
                    ) : (
                      <button key={page} onClick={() => handlePageChange(page)}
                        className={`w-8 h-8 flex items-center justify-center rounded-xl text-sm ${
                          pagination.currentPage === page
                            ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black"
                            : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                        }`}
                      >{page}</button>
                    )
                  )}
                </div>

                <PageBtn disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0} onClick={() => handlePageChange(pagination.currentPage + 1)} aria="Next page"><CaretRight size={16} /></PageBtn>
                <PageBtn disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0} onClick={() => handlePageChange(pagination.totalPages)} aria="Last page"><CaretDoubleRight size={16} /></PageBtn>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col text-sm items-center justify-center h-64 p-8">
            <div className="p-2 rounded-full bg-neutral-100 dark:bg-neutral-900/20 flex items-center justify-center mb-4">
              <ClockCounterClockwise size={24} className="text-neutral-600 dark:text-neutral-400" />
            </div>
            <h3 className="text-xl font-medium text-neutral-900 dark:text-white mb-2">No Audit Logs Found</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center max-w-md mb-6">
              {filtersActive
                ? "No logs match your current filters. Try adjusting your search or date range."
                : "No audit events have been recorded yet. Actions will appear here as they happen."}
            </p>
            {filtersActive && (
              <button
                onClick={resetAndRefresh}
                className="px-4 py-2 border border-neutral-200 dark:bg-neutral-800 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors flex items-center gap-2"
              >
                <ArrowClockwise size={16} />Reset Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Details modal */}
      {selectedLog && <DetailsModal log={selectedLog} onClose={() => setSelectedLog(null)} />}
    </div>
  )
}

export default AuditLog
